import {
  Box,
  FormControl,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  SelectChangeEvent,
  Stack,
  TablePagination,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import Paper from '@mui/material/Paper';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import VisibilityIcon from '@mui/icons-material/Visibility';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import { AxiosError, AxiosRequestConfig } from 'axios';
import Cookies from 'js-cookie';
import moment from 'moment';
import { Fragment, useEffect, useState } from 'react';
import { toast } from 'react-toastify';
import Loading from '../../components/loading';
import LayoutBase from '../../shared/layouts';
import { api } from '../../shared/services/api';
import { IResponseAjud } from '../../shared/services/types/response';
import { maskCPFCNPJ } from '../../shared/utils/mask';
import UndrawNoData from '../../assets/svg/undraw_no_data.svg';
import AuditIcon from './components/auditIcon';
import StatusBadge from './components/statusBadge';
import {
  StyledTableCellBody,
  StyledTableCellHead,
} from './components/styledTableCell';
import ExecutionLogsModal from './components/executionLogsModal';
import AjudAuditModal from './components/ajudAuditModal';
import AjudExecutionLogDetails from './components/executionLogDetails';
import { useAjudActions } from './hooks/useAjudActions';

interface Column {
  id:
  | 'court_order'
  | 'status'
  | 'type_record'
  | 'cnpj_cpf_oj'
  | 'partner_name'
  | 'partner_document'
  | 'shipping_date'
  | 'lock_value'
  | 'audit'
  | 'actions';
  label: string;
  minWidth?: number;
  align?: 'center' | 'right' | 'left';
}

const columns: readonly Column[] = [
  { id: 'court_order', label: 'Protocolo', minWidth: 150 },
  { id: 'status', label: 'Status', minWidth: 160, align: 'center' },
  { id: 'type_record', label: 'Tipo', minWidth: 100 },
  { id: 'cnpj_cpf_oj', label: 'CPF/CNPJ', minWidth: 150 },
  { id: 'partner_name', label: 'Parceiro', minWidth: 170 },
  { id: 'partner_document', label: 'Documento', minWidth: 150 },
  { id: 'shipping_date', label: 'Data Envio', minWidth: 140 },
  { id: 'lock_value', label: 'Valor', minWidth: 130, align: 'right' },
  { id: 'audit', label: 'Auditoria', minWidth: 80, align: 'center' },
  { id: 'actions', label: 'Ações', minWidth: 80, align: 'center' },
];

const typeRecordLabel: Record<string, string> = {
  '03': 'Bloqueio',
  '05': 'Desbloqueio',
  '07': 'Transferência',
};

const statusOptions = [
  { value: '', label: 'Todos' },
  { value: 'CREATED', label: 'Criado' },
  { value: 'PENDING', label: 'Pendente' },
  { value: 'DONE', label: 'Deferido' },
  { value: 'CANCEL', label: 'Cancelado' },
  { value: 'REFUSED', label: 'Recusado' },
  { value: 'ERROR', label: 'Erro' },
  { value: 'READY_TO_PROCESS', label: 'Pronto p/ Processar' },
  { value: 'AWAITING_MANUAL_ACCEPTANCE', label: 'Aguardando Aceite' },
];

const typeRecordOptions = [
  { value: '05', label: 'Desbloqueio' },
  { value: '', label: 'Todos' },
  { value: '03', label: 'Bloqueio' },
  { value: '07', label: 'Transferência' },
];

const AjudMonitoring = () => {
  const token = Cookies.get('@token');
  const [page, setPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(15);
  const [isLoading, setIsLoading] = useState(false);
  const [dataRows, setDataRows] = useState<IResponseAjud[]>([]);
  const [totalItems, setTotalItems] = useState(0);

  // Filtros que o backend suporta
  const [filterStatus, setFilterStatus] = useState('');
  const [filterTypeRecord, setFilterTypeRecord] = useState('05');
  const [filterCourtOrder, setFilterCourtOrder] = useState('');
  const [filterShippingDate, setFilterShippingDate] = useState('');
  const [filterDateProtocolization, setFilterDateProtocolization] = useState('');

  // Auditoria — mapa de id → boolean (tem logs ou não)
  const [auditMap, setAuditMap] = useState<Record<string, boolean>>({});

  // Modal de logs de execução
  const [logsModalOpen, setLogsModalOpen] = useState(false);
  const [selectedRecordId, setSelectedRecordId] = useState<string | null>(null);

  // Modal de conciliação / aceite manual
  const [auditModalOpen, setAuditModalOpen] = useState(false);
  const [auditRecord, setAuditRecord] = useState<IResponseAjud | null>(null);

  // Collapse — linha expandida para detalhes de execução
  const [expandedRowId, setExpandedRowId] = useState<string | null>(null);

  // Hook de ações (aceite manual, geração de resposta) com Dialog de confirmação e Snackbar
  const {
    handleManualAcceptance,
    // handleGenerateResponse,
    ConfirmationDialog,
    FeedbackSnackbar,
  } = useAjudActions(() => handleGetAll());

  const handleChangePage = (_event: unknown, newPage: number) => {
    setPage(newPage + 1);
  };

  const handleChangeRowsPerPage = (
    event: React.ChangeEvent<HTMLInputElement>,
  ) => {
    setRowsPerPage(Number(event.target.value));
    setPage(1);
  };

  useEffect(() => {
    handleGetAll();
  }, [page, rowsPerPage, filterStatus, filterTypeRecord, filterShippingDate, filterDateProtocolization]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleGetAll = async () => {
    const config: AxiosRequestConfig = {
      headers: {
        Authorization: 'Bearer ' + token,
        'Content-type': 'application/json',
        Accept: 'application/json',
      },
      params: {
        limit: rowsPerPage,
        page: page,
        ...(filterStatus && { status: filterStatus }),
        ...(filterTypeRecord && { type_record: filterTypeRecord }),
        ...(filterCourtOrder && { court_order: filterCourtOrder }),
        ...(filterShippingDate && { shipping_date: filterShippingDate }),
        ...(filterDateProtocolization && { date_protocolization: filterDateProtocolization }),
      },
    };

    try {
      setIsLoading(true);
      const response = await api.get('/ajudArchive/getAllMinimal', config);

      const responseData = response.data;
      const rows: IResponseAjud[] = responseData?.data || [];
      const total = responseData?.meta?.totalItems || 0;

      setDataRows(rows);
      setTotalItems(total);

      // Buscar status de auditoria para cada registro
      checkAuditLogs(rows);
    } catch (err: unknown) {
      if (err instanceof AxiosError) {
        const errorMessage = err.response?.data?.message || 'Erro de conexão';
        toast.error(errorMessage);
      } else {
        toast.error('Erro inesperado');
      }
    } finally {
      setIsLoading(false);
    }
  };

  const checkAuditLogs = async (rows: IResponseAjud[]) => {
    const headers = {
      Authorization: 'Bearer ' + token,
    };

    // Deduplica por court_order para evitar chamadas repetidas
    const uniqueCourtOrders = [...new Set(rows.map((r) => r.court_order).filter(Boolean))];

    const results = await Promise.allSettled(
      uniqueCourtOrders.map((courtOrder) =>
        api.get(`/ajudArchive/executionLogs/courtOrder/${courtOrder}`, { headers }),
      ),
    );

    // Monta mapa court_order → hasLogs
    const courtOrderMap: Record<string, boolean> = {};
    uniqueCourtOrders.forEach((courtOrder, index) => {
      const result = results[index];
      if (result.status === 'fulfilled') {
        const data = result.value.data;
        const logs = data?.logs ?? data;
        courtOrderMap[courtOrder] = Array.isArray(logs) ? logs.length > 0 : false;
      } else {
        courtOrderMap[courtOrder] = false;
      }
    });

    // Mapeia para cada row pelo court_order
    const newMap: Record<string, boolean> = {};
    rows.forEach((row) => {
      newMap[row.id] = courtOrderMap[row.court_order] ?? false;
    });

    setAuditMap(newMap);
  };

  const handleSearchCourtOrder = () => {
    setPage(1);
    handleGetAll();
  };

  const formatDate = (dateStr: string) => {
    if (!dateStr) return '-';

    // Tenta formato DDMMYYYY (8 dígitos puros)
    const digitsOnly = dateStr.replace(/\D/g, '');
    if (digitsOnly.length === 8) {
      const parsed = moment(digitsOnly, 'DDMMYYYY');
      if (parsed.isValid()) return parsed.format('DD/MM/YYYY');
    }

    // Tenta ISO / outros formatos reconhecidos pelo moment
    const parsed = moment(dateStr);
    if (parsed.isValid()) return parsed.format('DD/MM/YYYY');

    return '-';
  };

  const handleOpenLogs = (recordId: string) => {
    setSelectedRecordId(recordId);
    setLogsModalOpen(true);
  };

  const handleOpenAuditModal = (row: IResponseAjud) => {
    setAuditRecord(row);
    setAuditModalOpen(true);
  };

  const handleAuditConfirm = (recordId: string, retainedValue: number) => {
    const record = dataRows.find((r) => r.id === recordId);
    handleManualAcceptance(recordId, retainedValue, record?.court_order || '');
  };

  const renderCellValue = (column: Column, row: IResponseAjud) => {
    switch (column.id) {
      case 'status':
        return <StatusBadge status={row.status} />;
      case 'type_record':
        return typeRecordLabel[row.type_record] ?? row.type_record;
      case 'cnpj_cpf_oj':
        return row.type_record === '07' ? '-' : maskCPFCNPJ(row.cnpj_cpf_oj);
      case 'partner_document':
        return maskCPFCNPJ(row.partner_document);
      case 'shipping_date':
        return formatDate(row.shipping_date);
      case 'lock_value': {
        const raw =
          row.type_record === '05' ? row.value_unlock
          : row.type_record === '07' ? row.value_transference
          : row.lock_value;
        if (raw == null || raw === '') return '-';
        // Suporta formato BR (57.118,60) e padrão (57118.60)
        const normalized = String(raw).includes(',')
          ? String(raw).replace(/\./g, '').replace(',', '.')
          : String(raw);
        const val = Number(normalized);
        return !isNaN(val)
          ? `R$ ${val.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}`
          : '-';
      }
      case 'audit':
        return <AuditIcon hasAuditLog={auditMap[row.id] ?? false} />;
      case 'actions':
        return (
          <Tooltip title="Ver logs de execução">
            <IconButton size="small" onClick={() => handleOpenLogs(row.id)}>
              <VisibilityIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        );
      default:
        return row[column.id as keyof IResponseAjud]?.toString() || '-';
    }
  };

  return (
    <LayoutBase>
      <Typography color="textPrimary" variant="h5">
        AJUD
      </Typography>
      <Typography sx={{ marginBottom: 2 }}>Monitoramento</Typography>

      <Stack direction="row" spacing={2} sx={{ mb: 2 }} alignItems="center" flexWrap="wrap">
        <FormControl size="small" sx={{ minWidth: 180 }}>
          <InputLabel>Status</InputLabel>
          <Select
            value={filterStatus}
            label="Status"
            onChange={(e: SelectChangeEvent) => {
              setFilterStatus(e.target.value);
              setPage(1);
            }}
          >
            {statusOptions.map((opt) => (
              <MenuItem key={opt.value} value={opt.value}>
                {opt.label}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        <FormControl size="small" sx={{ minWidth: 160 }}>
          <InputLabel>Tipo</InputLabel>
          <Select
            value={filterTypeRecord}
            label="Tipo"
            onChange={(e: SelectChangeEvent) => {
              setFilterTypeRecord(e.target.value);
              setPage(1);
            }}
          >
            {typeRecordOptions.map((opt) => (
              <MenuItem key={opt.value} value={opt.value}>
                {opt.label}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        <TextField
          label="Protocolo"
          size="small"
          value={filterCourtOrder}
          onChange={(e) => setFilterCourtOrder(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter') handleSearchCourtOrder();
          }}
          placeholder="Buscar por protocolo..."
          sx={{ width: 220 }}
        />

        <TextField
          label="Data Remessa"
          size="small"
          type="date"
          value={filterShippingDate}
          onChange={(e) => {
            setFilterShippingDate(e.target.value);
            setPage(1);
          }}
          InputLabelProps={{ shrink: true }}
          sx={{ width: 180 }}
        />

        <TextField
          label="Data Protocolo"
          size="small"
          type="date"
          value={filterDateProtocolization}
          onChange={(e) => {
            setFilterDateProtocolization(e.target.value);
            setPage(1);
          }}
          InputLabelProps={{ shrink: true }}
          sx={{ width: 180 }}
        />
      </Stack>

      <Loading isLoading={isLoading} />

      {dataRows.length === 0 && !isLoading ? (
        <>
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              width: '100%',
            }}
          >
            <Box
              component="img"
              src={UndrawNoData}
              alt="Sem dados"
              sx={{
                maxWidth: '15%',
                height: 'auto',
                marginTop: 10,
              }}
            />
          </Box>
          <Typography textAlign="center" sx={{ marginTop: 2 }}>
            Dados não encontrados...
          </Typography>
        </>
      ) : (
        <Paper sx={{ width: '100%', overflow: 'hidden' }}>
          <TableContainer sx={{ maxHeight: 500 }}>
            <Table stickyHeader aria-label="ajud monitoring table">
              <TableHead>
                <TableRow>
                  <StyledTableCellHead sx={{ width: 40 }} />
                  {columns.map((column) => (
                    <StyledTableCellHead
                      key={column.id}
                      align={column.align}
                      style={{ minWidth: column.minWidth }}
                    >
                      {column.label}
                    </StyledTableCellHead>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {dataRows.map((row) => {
                  const isExpanded = expandedRowId === row.id;
                  return (
                    <Fragment key={row.id}>
                      <TableRow
                        hover
                        tabIndex={-1}
                        onClick={() => {
                          if (row.type_record === '05' || row.type_record === '07') {
                            handleOpenAuditModal(row);
                          }
                        }}
                        sx={{
                          cursor: row.type_record === '05' || row.type_record === '07' ? 'pointer' : 'default',
                          '& > *': { borderBottom: isExpanded ? 'unset' : undefined },
                        }}
                      >
                        <TableCell sx={{ width: 40, p: 0.5 }}>
                          <IconButton
                            size="small"
                            onClick={(e) => {
                              e.stopPropagation();
                              setExpandedRowId(isExpanded ? null : row.id);
                            }}
                          >
                            {isExpanded ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
                          </IconButton>
                        </TableCell>
                        {columns.map((column) => (
                          <StyledTableCellBody key={column.id} align={column.align}>
                            {renderCellValue(column, row)}
                          </StyledTableCellBody>
                        ))}
                      </TableRow>
                      <TableRow key={`${row.id}-details`}>
                        <TableCell
                          colSpan={columns.length + 1}
                          sx={{ py: 0, px: 1, borderBottom: isExpanded ? undefined : 'none' }}
                        >
                          <AjudExecutionLogDetails open={isExpanded} row={row} />
                        </TableCell>
                      </TableRow>
                    </Fragment>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
          <TablePagination
            rowsPerPageOptions={[15, 30, 50]}
            component="div"
            count={totalItems}
            rowsPerPage={rowsPerPage}
            page={page - 1}
            onPageChange={handleChangePage}
            onRowsPerPageChange={handleChangeRowsPerPage}
          />
        </Paper>
      )}

      <ExecutionLogsModal
        open={logsModalOpen}
        onClose={() => {
          setLogsModalOpen(false);
          setSelectedRecordId(null);
        }}
        recordId={selectedRecordId}
      />

      <AjudAuditModal
        open={auditModalOpen}
        onClose={() => {
          setAuditModalOpen(false);
          setAuditRecord(null);
        }}
        record={auditRecord}
        hasAuditLog={auditRecord ? (auditMap[auditRecord.id] ?? false) : false}
        onConfirm={handleAuditConfirm}
      />

      <ConfirmationDialog />
      <FeedbackSnackbar />
    </LayoutBase>
  );
};

export default AjudMonitoring;
